import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Authorize, Table } from '@/components';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';
import {
  useCommerces,
  useCommercesPagination,
  useDeleteCommerce,
} from '../hooks';
import {
  useGetLogisticOperatorByCommerceQuery,
  useGetMeliSellersByCommerceCodeQuery,
} from '../services';
import { commercesTableColumns } from '../config';
import { Commerce, CommerceFilters } from '../models';
import {
  formatCommerceSellerIds,
  getCommerceIntegrationCode,
  toCommerceQueryFilters,
} from '../utils';

interface CommercesTableProps {
  filters: CommerceFilters;
}

const CommercesTable = ({ filters }: CommercesTableProps) => {
  const navigate = useNavigate();
  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    useCommercesPagination();

  const { handleDeleteCommerce, isLoading: isDeletingCommerce } =
    useDeleteCommerce({
      onSuccess: () => {},
    });

  const hasActiveFilters = Object.values(filters).some(
    (v) => typeof v === 'string' && v.trim() !== ''
  );

  const {
    data: sellersByCode,
    isLoading: isLoadingSellers,
    isError: isSellersError,
  } = useGetMeliSellersByCommerceCodeQuery();

  const filtersBySellerId = !!filters.sellerId?.trim();
  const isWaitingForSellers = filtersBySellerId && !sellersByCode;

  const filtersToSend = useMemo(
    () =>
      hasActiveFilters
        ? toCommerceQueryFilters(filters, sellersByCode ?? {})
        : undefined,
    [filters, hasActiveFilters, sellersByCode]
  );

  const {
    commerces,
    isLoading: isLoadingCommerces,
    error,
    totalItems,
    totalPages,
  } = useCommerces({
    page: currentPage,
    pageSize,
    filters: filtersToSend,
    skip: isWaitingForSellers,
  });

  const isLoading =
    isLoadingCommerces || (isWaitingForSellers && !isSellersError);

  const hasTaxNumberInPage = commerces.some((commerce) =>
    commerce.taxNumber?.trim()
  );

  /**
   * La columna RUC se oculta cuando ninguna fila cargada la tiene.
   *
   * La decisión se recuerda entre cargas a propósito: si se recalculara
   * mientras `isLoading`, con la lista todavía vacía, el skeleton se dibujaría
   * sin esa columna y la tabla saltaría de ancho al llegar las filas.
   */
  const showTaxNumberRef = useRef(true);
  if (!isLoading) {
    showTaxNumberRef.current = hasTaxNumberInPage;
  }
  const showTaxNumber = showTaxNumberRef.current;

  const handleEdit = (commerceId: string) => {
    navigate(`/business/commerce/${commerceId}/edit`);
  };

  const handleDeleteConfirm = (commerce: Commerce) => {
    if (
      window.confirm(
        `¿Estás seguro de que quieres eliminar el comercio "${commerce.name}"?`
      )
    ) {
      handleDeleteCommerce(commerce.id);
    }
  };

  const handleView = (commerceId: string) => {
    navigate(`/business/commerce/${commerceId}/dashboard`);
  };

  const LogisticOperatorCell = ({ commerceId }: { commerceId: string }) => {
    const { data: associations } = useGetLogisticOperatorByCommerceQuery(
      { commerceId },
      { skip: !commerceId }
    );

    const association = associations?.[0];

    if (!association) {
      return (
        <span className='text-sm text-gray-500 italic'>
          Sin operador asociado
        </span>
      );
    }

    return (
      <div>
        <p className='text-sm font-medium text-gray-900'>
          {association.logisticOperator.name}
        </p>
        <p className='text-xs text-gray-500'>
          {association.logisticOperator.businessName}
        </p>
      </div>
    );
  };

  const columns = useMemo(() => {
    const baseColumns = commercesTableColumns.filter(
      (column) => column.field !== 'taxNumber' || showTaxNumber
    );

    const logisticOperatorIndex = baseColumns.findIndex(
      (col) => col.field === 'logisticOperator'
    );

    if (logisticOperatorIndex !== -1) {
      baseColumns[logisticOperatorIndex] = {
        ...baseColumns[logisticOperatorIndex],
        render: (_value: any, rowData: Commerce) => (
          <LogisticOperatorCell commerceId={rowData.id} />
        ),
      };
    }

    const sellerIdsIndex = baseColumns.findIndex(
      (col) => col.field === 'sellerIds'
    );

    if (sellerIdsIndex !== -1) {
      baseColumns[sellerIdsIndex] = {
        ...baseColumns[sellerIdsIndex],
        render: (_value: unknown, rowData: Commerce) => {
          if (isLoadingSellers) return '…';
          return (
            formatCommerceSellerIds(
              sellersByCode?.[getCommerceIntegrationCode(rowData)]
            ) || '-'
          );
        },
      };
    }

    return baseColumns;
  }, [showTaxNumber, sellersByCode, isLoadingSellers]);

  const renderActions = (rowData: Commerce) => (
    <div className='flex gap-1'>
      <IconButton onClick={() => handleView(rowData.id)} size='small'>
        <VisibilityIcon fontSize='small' />
      </IconButton>
      <Authorize permission={BUSINESS_ROLE_GROUPS.operations} fallback={null}>
        <IconButton onClick={() => handleEdit(rowData.id)} size='small'>
          <EditIcon fontSize='small' />
        </IconButton>
        <IconButton
          onClick={() => handleDeleteConfirm(rowData)}
          size='small'
          disabled={isDeletingCommerce}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Authorize>
    </div>
  );

  if (error) {
    return (
      <div className='text-center py-8'>
        <p className='text-red-600'>Error al cargar los comercios</p>
      </div>
    );
  }

  if (filtersBySellerId && isSellersError) {
    return (
      <div className='text-center py-8'>
        <p className='text-red-600'>
          No se pudo consultar Mercado Libre para buscar por Seller ID. Probá de
          nuevo en un rato o buscá por otro dato.
        </p>
      </div>
    );
  }

  return (
    <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
      {/*
        Mientras carga se muestra la tabla con su skeleton: el "no hay
        comercios" solo corresponde cuando la carga terminó y de verdad no vino
        ninguno.
      */}
      {isLoading || commerces.length > 0 ? (
        <Table
          data={commerces}
          columns={columns}
          renderActions={renderActions}
          isLoading={isLoading}
          // Alto parecido al de la tabla real, sin dibujar 50 filas fantasma.
          skeletonRows={Math.min(pageSize, 10)}
          pagination={{
            totalItems,
            totalPages,
            currentPage,
            rowsPerPage: pageSize,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handlePageSizeChange,
          }}
        />
      ) : (
        <div className='text-center text-gray-500 py-8'>
          No hay comercios por el momento
        </div>
      )}
    </div>
  );
};

export default CommercesTable;
